import React, { useState, useEffect } from 'react';
import { Clock, XCircle, CheckCircle, TrendingUp, TrendingDown, RefreshCw } from 'lucide-react';
import { COLORS } from '../../../../utils/constants';
import { adminAPI } from '../../../../services/api';

const OrdersTab = ({ clientId }) => {
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    loadOrders();
  }, [clientId]);

  const loadOrders = async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const [futuresRes, spotRes] = await Promise.allSettled([
        adminAPI.getClientPendingOrders(clientId),
        adminAPI.getClientSpotOrders(clientId)
      ]);
      
      const futuresOrders = futuresRes.status === 'fulfilled' ? (futuresRes.value.data?.orders || []) : [];
      const spotOrders = spotRes.status === 'fulfilled' ? (spotRes.value.data?.orders || []) : [];
      
      setOrders([
        ...futuresOrders.map(o => ({ ...o, market: 'futures' })),
        ...spotOrders.map(o => ({ ...o, market: 'spot' }))
      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (err) {
      console.error('Failed to load orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (orderId, market) => {
    try {
      if (market === 'spot') {
        await adminAPI.cancelSpotOrder(clientId, orderId);
      } else {
        await adminAPI.cancelFuturesOrder(clientId, orderId);
      }
      loadOrders();
    } catch (err) {
      console.error('Cancel failed:', err);
    }
  };

  const pendingCount = orders.filter(o => o.status === 'pending' || o.status === 'open').length;

  return (
    <div style={{ padding: '8px 0' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <span style={{ color: COLORS.text, fontSize: '14px', fontWeight: '600' }}>
          Ордера ({orders.length}) {pendingCount > 0 && <span style={{ color: COLORS.warning }}>• {pendingCount} ожид.</span>}
        </span>
        <button onClick={loadOrders} disabled={loading} style={{ background: 'none', border: 'none', color: COLORS.textSecondary, cursor: 'pointer', padding: '4px' }}>
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Orders List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px', color: COLORS.textSecondary, fontSize: '13px' }}>Загрузка...</div>
      ) : orders.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '20px', color: COLORS.textSecondary, fontSize: '13px' }}>Нет ордеров</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {orders.slice(0, 10).map((order, idx) => {
            const isPending = order.status === 'pending' || order.status === 'open';
            const isBuy = order.side === 'buy' || order.side === 'long';
            return (
              <div key={order.id || idx} style={{ 
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '8px 10px', backgroundColor: COLORS.background, borderRadius: '6px',
                borderLeft: `3px solid ${isPending ? COLORS.warning : order.status === 'cancelled' ? COLORS.danger : COLORS.success}`
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {isBuy ? <TrendingUp size={14} color={COLORS.success} /> : <TrendingDown size={14} color={COLORS.danger} />}
                  <div>
                    <div style={{ color: COLORS.text, fontSize: '12px', fontWeight: '600' }}>{order.symbol}</div>
                    <div style={{ color: COLORS.textSecondary, fontSize: '10px' }}>
                      {(order.quantity || order.amount || 0).toFixed(4)} @ ${(order.limit_price || order.price || 0).toLocaleString()}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {isPending ? (
                    <>
                      <Clock size={12} color={COLORS.warning} />
                      <button onClick={() => handleCancel(order.id, order.market)} style={{ padding: '2px 6px', backgroundColor: `${COLORS.danger}20`, border: 'none', borderRadius: '3px', color: COLORS.danger, fontSize: '10px', cursor: 'pointer' }}>✕</button>
                    </>
                  ) : order.status === 'cancelled' ? (
                    <XCircle size={12} color={COLORS.danger} />
                  ) : (
                    <CheckCircle size={12} color={COLORS.success} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OrdersTab;
